import { useState, useEffect } from 'react'
import { Link, Routes, Route, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { User, LogOut, Upload, FileText, Settings, BarChart3 } from 'lucide-react'
import UploadNotes from '../components/UploadNotes'
import TaskReviewEnhanced from '../components/TaskReviewEnhanced'
import TrelloConnect from '../components/TrelloConnect'
import TaskHistory from '../components/TaskHistory'
import Success from './Success'
import { trelloService } from '../lib/trello'
import Logo from '../components/Logo'

export default function Dashboard() {
  const [user, setUser] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [trelloConnected, setTrelloConnected] = useState(false)
  const [showTrelloConnect, setShowTrelloConnect] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    const getUser = async () => {
      setLoading(true)
      const { data: { user } } = await supabase.auth.getUser()
      setUser(user)
      try {
        await trelloService.init(); // Fetch Trello token from DB
        setTrelloConnected(trelloService.isConnected())
      } catch (error) {
        // If token is invalid, it will be cleared automatically
        setTrelloConnected(false)
      }
      setLoading(false)
    }
    getUser()
  }, [])

  // Reset TrelloConnect modal when connection status changes
  useEffect(() => {
    if (trelloConnected) {
      setShowTrelloConnect(false)
    }
  }, [trelloConnected])

  // Reset TrelloConnect modal when navigating away from dashboard
  useEffect(() => {
    const handleBeforeUnload = () => {
      setShowTrelloConnect(false)
    }
    
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      setShowTrelloConnect(false)
    }
  }, [])

  const handleSignOut = async () => {
    await supabase.auth.signOut()
    trelloService.clearToken(); // Clear in-memory token and stop sync
    navigate('/')
  }

  const handleTrelloConnect = async (token: string) => {
    trelloService.setToken(token)
    await refreshTrelloStatus() // Refresh the connection status
    setShowTrelloConnect(false)
  }

  const handleTrelloSkip = () => {
    setShowTrelloConnect(false)
  }

  const refreshTrelloStatus = async () => {
    try {
      await trelloService.init(); // Re-initialize to check connection
      setTrelloConnected(trelloService.isConnected())
    } catch (error) {
      console.error('Error refreshing Trello status:', error)
      setTrelloConnected(false)
    }
  }

  const handleDisconnectTrello = async () => {
    if (!user?.id) return;
    
    // Clear token from database
    await supabase.from('user_trello_tokens').delete().eq('user_id', user.id);
    
    // Clear all user data (tokens, members, periodic sync)
    await trelloService.clearAllUserData();
    
    setTrelloConnected(false);
    console.log('🔌 Trello account disconnected and data cleared');
  };

  // Helper function to determine if a tab is active
  const isActiveTab = (path: string) => {
    if (path === '/dashboard' && location.pathname === '/dashboard') return true;
    if (path !== '/dashboard' && location.pathname.startsWith(path)) return true;
    return false;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-spinner"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/dashboard" className="flex items-center space-x-2">
                <Logo />
              </Link>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-gray-600" />
                </div>
                <span className="text-sm text-gray-700">{user?.email}</span>
              </div>
              <button
                onClick={handleSignOut}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-800"
              >
                <LogOut className="w-4 h-4" />
                <span className="text-sm">Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4">
          <div className="flex space-x-8">
            <Link
              to="/dashboard"
              className={`flex items-center space-x-2 py-4 px-2 border-b-2 transition-colors ${
                isActiveTab('/dashboard')
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
              }`}
            >
              <Upload className="w-4 h-4" />
              <span>Upload Notes</span>
            </Link>
            <Link
              to="/dashboard/sprint-report"
              className={`flex items-center space-x-2 py-4 px-2 border-b-2 transition-colors ${
                isActiveTab('/dashboard/sprint-report')
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
              }`}
            >
              <BarChart3 className="w-4 h-4" />
              <span>Sprint Report</span>
            </Link>
            <Link
              to="/dashboard/history"
              className={`flex items-center space-x-2 py-4 px-2 border-b-2 transition-colors ${
                isActiveTab('/dashboard/history')
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
              }`}
            >
              <FileText className="w-4 h-4" />
              <span>History</span>
            </Link>
            <Link
              to="/dashboard/settings"
              className={`flex items-center space-x-2 py-4 px-2 border-b-2 transition-colors ${
                isActiveTab('/dashboard/settings')
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
              }`}
            >
              <Settings className="w-4 h-4" />
              <span>Settings</span>
            </Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {showTrelloConnect ? (
          <TrelloConnect 
            onConnect={handleTrelloConnect}
            onSkip={handleTrelloSkip}
          />
        ) : (
          <div>
            {/* Trello Connection Prompt */}
            {!trelloConnected && (
              <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-base font-medium text-blue-900 mb-1">
                      🔗 Connect Your Trello Account
                    </h3>
                    <p className="text-sm text-blue-700">
                      Connect to start creating cards in your Trello boards.
                    </p>
                  </div>
                  <button
                    onClick={() => setShowTrelloConnect(true)}
                    className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-100 border border-blue-300 rounded-md hover:bg-blue-200"
                  >
                    Connect Trello
                  </button>
                </div>
              </div>
            )}

            {/* Connection Status */}
            {trelloConnected && (
              <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs">✓</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-green-900">
                        Connected to Trello
                      </p>
                      <p className="text-xs text-green-700">
                        Ready to create cards in your boards
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Render nested routes */}
            <Outlet />
          </div>
        )}
      </main>
    </div>
  )
} 